import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit, ToggleLeft, ToggleRight, History, Users } from 'lucide-react';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import Input from '../components/common/Input';
import { Dependent, DependentFormData, DependentLog } from '../types';
import api from '../services/api';
import { dependentService } from '../services/dependentService';
import { formatCPF, formatPhone, formatDate } from '../utils/formatters';

const emptyForm: DependentFormData = {
  nome: '',
  cpf: '',
  data_nascimento: '',
  parentesco: '',
  email: '',
  telefone: '',
  password: '',
  password_confirmation: '',
};

const AssociateDependents: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [associateName, setAssociateName] = useState('');
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState<Dependent | null>(null);
  const [editingDependent, setEditingDependent] = useState<Dependent | null>(null);
  const [processing, setProcessing] = useState(false);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeNotes, setStatusChangeNotes] = useState('');
  const [dependentLogs, setDependentLogs] = useState<DependentLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [formData, setFormData] = useState<DependentFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadDependents();
    loadAssociateInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAssociateInfo = async () => {
    try {
      const response = await api.get(`/associates/${id}`);
      setAssociateName(response.data.nome || response.data.name);
    } catch (err) {
      console.error('Erro ao carregar informações do associado');
    }
  };

  const loadDependents = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await dependentService.getAll(parseInt(id));
      setDependents(data);
    } catch (err: any) {
      setError('Erro ao carregar dependentes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.nome.trim()) errors.nome = 'Nome é obrigatório';
    if (!formData.cpf.trim()) errors.cpf = 'CPF é obrigatório';
    if (!formData.data_nascimento) errors.data_nascimento = 'Data de nascimento é obrigatória';
    if (!formData.parentesco.trim()) errors.parentesco = 'Parentesco é obrigatório';
    if (!formData.email.trim()) errors.email = 'E-mail é obrigatório';

    // Senha é opcional na criação: se não for informada, o dependente a cria
    // no primeiro login. Só validamos quando o admin escolhe defini-la agora.
    if (!editingDependent && formData.password) {
      if (formData.password.length < 6) {
        errors.password = 'Senha deve ter ao menos 6 caracteres';
      }
      if (formData.password !== formData.password_confirmation) {
        errors.password_confirmation = 'As senhas não conferem';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setEditingDependent(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEditModal = (dependent: Dependent) => {
    setEditingDependent(dependent);
    setFormData({
      nome: dependent.nome || dependent.name,
      cpf: dependent.cpf,
      data_nascimento: dependent.data_nascimento || dependent.birth_date || '',
      parentesco: dependent.parentesco || dependent.kinship,
      email: dependent.email,
      telefone: dependent.telefone || dependent.phone || '',
      password: '',
      password_confirmation: '',
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const handleSubmit = async () => {
    if (!id || !validate()) return;

    setProcessing(true);
    try {
      if (editingDependent) {
        await dependentService.update(parseInt(id), parseInt(editingDependent.id), formData);
        setSuccess('Dependente atualizado com sucesso');
      } else {
        await dependentService.create(parseInt(id), formData);
        setSuccess('Dependente cadastrado com sucesso');
      }
      await loadDependents();
      setShowFormModal(false);
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar dependente');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemove = async () => {
    if (!id || !selectedDependent) return;

    setProcessing(true);
    try {
      await dependentService.delete(parseInt(id), parseInt(selectedDependent.id));
      setSuccess('Dependente removido com sucesso');
      await loadDependents();
      setShowRemoveModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao remover dependente');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!id || !selectedDependent) return;

    if (!statusChangeReason.trim() || statusChangeReason.trim().length < 5) {
      setError('O motivo é obrigatório e deve ter pelo menos 5 caracteres');
      return;
    }

    const newStatus = selectedDependent.status === 'ativo' ? 'inativo' : 'ativo';
    const actionText = newStatus === 'ativo' ? 'reativado' : 'inativado';

    setProcessing(true);
    try {
      await dependentService.updateStatus(
        parseInt(id),
        parseInt(selectedDependent.id),
        newStatus,
        statusChangeReason.trim(),
        statusChangeNotes.trim() || undefined
      );
      setSuccess(`Dependente ${actionText} com sucesso`);
      await loadDependents();
      setShowStatusModal(false);
      setStatusChangeReason('');
      setStatusChangeNotes('');
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.errors?.reason?.[0] ||
          'Erro ao alterar status do dependente'
      );
    } finally {
      setProcessing(false);
    }
  };

  const loadLogs = async (dependent: Dependent) => {
    if (!id) return;

    try {
      setLoadingLogs(true);
      setSelectedDependent(dependent);
      const logs = await dependentService.getLogs(parseInt(id), parseInt(dependent.id));
      setDependentLogs(logs);
      setShowLogsModal(true);
    } catch (err) {
      setError('Erro ao carregar histórico. Tente novamente.');
    } finally {
      setLoadingLogs(false);
    }
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'ativo':
        return 'success';
      case 'inativo':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'ativo':
        return 'Ativo';
      case 'inativo':
        return 'Inativo';
      default:
        return status || '';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created':
        return 'Dependente Criado';
      case 'updated':
        return 'Dados Atualizados';
      case 'inactivated':
        return 'Inativado';
      case 'reactivated':
        return 'Reativado';
      case 'inactivated_by_cascade':
        return 'Inativado Automaticamente (Titular Inativado)';
      case 'reactivated_by_cascade':
        return 'Reativado Automaticamente (Titular Reativado)';
      case 'deleted':
        return 'Removido';
      default:
        return action;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button variant="secondary" onClick={() => navigate('/associados')} className="mb-4">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Voltar
        </Button>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dependentes do Associado</h1>
            <p className="text-gray-600">{associateName}</p>
          </div>
          <Button onClick={openCreateModal}>
            <Plus className="w-5 h-5 mr-2" />
            Adicionar Dependente
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')} className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} className="mb-6">
          {success}
        </Alert>
      )}

      {dependents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum dependente cadastrado</h3>
          <p className="text-gray-600 mb-4">Este associado ainda não possui dependentes</p>
          <Button onClick={openCreateModal}>
            <Plus className="w-5 h-5 mr-2" />
            Adicionar Primeiro Dependente
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {dependents.map((dependent) => (
            <div
              key={dependent.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {dependent.nome || dependent.name}
                  </h3>
                  <p className="text-sm text-gray-600">{formatCPF(dependent.cpf)}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant={getStatusBadgeVariant(dependent.status)}>
                    {getStatusLabel(dependent.status)}
                  </Badge>
                  {dependent.has_password === false && (
                    <Badge variant="warning">Aguardando 1º acesso</Badge>
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                <p>
                  <span className="font-medium text-gray-700">Parentesco:</span>{' '}
                  {dependent.parentesco || dependent.kinship}
                </p>
                <p>
                  <span className="font-medium text-gray-700">E-mail:</span> {dependent.email}
                </p>
                {(dependent.telefone || dependent.phone) && (
                  <p>
                    <span className="font-medium text-gray-700">Telefone:</span>{' '}
                    {formatPhone(dependent.telefone || dependent.phone || '')}
                  </p>
                )}
                {(dependent.data_nascimento || dependent.birth_date) && (
                  <p>
                    <span className="font-medium text-gray-700">Nascimento:</span>{' '}
                    {formatDate(dependent.data_nascimento || dependent.birth_date || '')}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openEditModal(dependent)} title="Editar">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant={dependent.status === 'ativo' ? 'secondary' : 'primary'}
                  onClick={() => {
                    setSelectedDependent(dependent);
                    setShowStatusModal(true);
                  }}
                  className="flex-1"
                  title={dependent.status === 'ativo' ? 'Inativar dependente' : 'Reativar dependente'}
                >
                  {dependent.status === 'ativo' ? (
                    <ToggleLeft className="w-4 h-4 mr-1" />
                  ) : (
                    <ToggleRight className="w-4 h-4 mr-1" />
                  )}
                  {dependent.status === 'ativo' ? 'Inativar' : 'Reativar'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadLogs(dependent)}
                  title="Ver histórico"
                  disabled={loadingLogs}
                >
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setSelectedDependent(dependent);
                    setShowRemoveModal(true);
                  }}
                  title="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Criar/Editar Dependente */}
      {showFormModal && (
        <Modal
          isOpen={showFormModal}
          onClose={() => {
            setShowFormModal(false);
            resetForm();
          }}
          title={editingDependent ? 'Editar Dependente' : 'Adicionar Dependente'}
        >
          <div className="space-y-4">
            <Input
              label="Nome Completo"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              error={formErrors.nome}
              required
            />
            <Input
              label="CPF"
              name="cpf"
              value={formData.cpf}
              onChange={handleChange}
              error={formErrors.cpf}
              required
            />
            <Input
              label="Data de Nascimento"
              type="date"
              name="data_nascimento"
              value={formData.data_nascimento}
              onChange={handleChange}
              error={formErrors.data_nascimento}
              required
            />
            <Input
              label="Parentesco"
              name="parentesco"
              placeholder="Ex: Filho(a), Cônjuge..."
              value={formData.parentesco}
              onChange={handleChange}
              error={formErrors.parentesco}
              required
            />
            <Input
              label="E-mail"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={formErrors.email}
              required
            />
            <Input
              label="Telefone"
              name="telefone"
              value={formData.telefone || ''}
              onChange={handleChange}
              error={formErrors.telefone}
            />

            {!editingDependent && (
              <>
                <div>
                  <Input
                    label="Senha de Acesso (opcional)"
                    type="password"
                    name="password"
                    value={formData.password || ''}
                    onChange={handleChange}
                    error={formErrors.password}
                    placeholder="Deixe em branco para o dependente criar no 1º acesso"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Se não for definida agora, o próprio dependente cria a senha ao tentar entrar pela primeira vez.
                  </p>
                </div>
                {formData.password && (
                  <Input
                    label="Confirmar Senha"
                    type="password"
                    name="password_confirmation"
                    value={formData.password_confirmation || ''}
                    onChange={handleChange}
                    error={formErrors.password_confirmation}
                    required
                  />
                )}
              </>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowFormModal(false);
                  resetForm();
                }}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button onClick={handleSubmit} loading={processing} disabled={processing} className="flex-1">
                {editingDependent ? 'Salvar Alterações' : 'Adicionar Dependente'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Alterar Status */}
      {showStatusModal && selectedDependent && (
        <Modal
          isOpen={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setStatusChangeReason('');
            setStatusChangeNotes('');
          }}
          title={selectedDependent.status === 'ativo' ? 'Inativar Dependente' : 'Reativar Dependente'}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Você está prestes a{' '}
              <strong>{selectedDependent.status === 'ativo' ? 'inativar' : 'reativar'}</strong> o dependente{' '}
              <strong>{selectedDependent.nome || selectedDependent.name}</strong>.
            </p>
            {selectedDependent.status === 'ativo' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Atenção:</strong> Ao inativar, o dependente perde acesso ao login, mas o histórico é
                  mantido.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Observação:</strong> Ao reativar, o dependente volta a ter acesso ao login.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo <span className="text-red-500">*</span>
              </label>
              <textarea
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                placeholder="Informe o motivo da alteração (mínimo 5 caracteres)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                maxLength={500}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{statusChangeReason.length}/500 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações Adicionais (opcional)
              </label>
              <textarea
                value={statusChangeNotes}
                onChange={(e) => setStatusChangeNotes(e.target.value)}
                placeholder="Informações complementares..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={2}
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1">{statusChangeNotes.length}/1000 caracteres</p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowStatusModal(false)}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                variant={selectedDependent.status === 'ativo' ? 'danger' : 'primary'}
                onClick={handleToggleStatus}
                loading={processing}
                disabled={processing}
                className="flex-1"
              >
                Confirmar {selectedDependent.status === 'ativo' ? 'Inativação' : 'Reativação'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Remover Dependente */}
      {showRemoveModal && selectedDependent && (
        <Modal isOpen={showRemoveModal} onClose={() => setShowRemoveModal(false)} title="Remover Dependente">
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja remover o dependente{' '}
              <strong>{selectedDependent.nome || selectedDependent.name}</strong>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Atenção:</strong> Esta ação remove permanentemente o dependente e seu acesso de login. Se
                deseja apenas suspender temporariamente, use o botão "Inativar" ao invés de remover.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowRemoveModal(false)}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleRemove}
                loading={processing}
                disabled={processing}
                className="flex-1"
              >
                Confirmar Remoção
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Histórico */}
      {showLogsModal && selectedDependent && (
        <Modal
          isOpen={showLogsModal}
          onClose={() => {
            setShowLogsModal(false);
            setDependentLogs([]);
          }}
          title={`Histórico - ${selectedDependent.nome || selectedDependent.name}`}
        >
          <div className="space-y-4">
            {loadingLogs ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : dependentLogs.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">Nenhum registro de alteração encontrado.</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {dependentLogs.map((log, index) => (
                  <div key={log.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 relative">
                    {index < dependentLogs.length - 1 && (
                      <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gray-300" />
                    )}

                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 z-10">
                        <History className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-gray-900">{getActionLabel(log.action)}</h4>
                            <p className="text-sm text-gray-500">
                              {new Date(log.created_at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          {log.previous_status && log.new_status && (
                            <div className="flex items-center gap-2">
                              <Badge variant={getStatusBadgeVariant(log.previous_status)}>
                                {getStatusLabel(log.previous_status)}
                              </Badge>
                              <span className="text-gray-400">→</span>
                              <Badge variant={getStatusBadgeVariant(log.new_status)}>
                                {getStatusLabel(log.new_status)}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {(log.user || log.metadata?.user_name) && (
                      <div className="ml-11 mb-2">
                        <p className="text-sm text-gray-600">
                          <strong>Usuário:</strong> {log.user?.name || log.metadata?.user_name}
                          {(log.user?.email || log.metadata?.user_email) && (
                            <span className="text-gray-500">
                              {' '}
                              ({log.user?.email || log.metadata?.user_email})
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    {log.reason && (
                      <div className="ml-11 mb-2">
                        <p className="text-sm">
                          <strong className="text-gray-700">Motivo:</strong>
                          <span className="text-gray-600 ml-2">{log.reason}</span>
                        </p>
                      </div>
                    )}

                    {log.notes && (
                      <div className="ml-11 mb-2">
                        <p className="text-sm">
                          <strong className="text-gray-700">Observações:</strong>
                          <span className="text-gray-600 ml-2">{log.notes}</span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLogsModal(false);
                  setDependentLogs([]);
                }}
                className="w-full"
              >
                Fechar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AssociateDependents;
